import './NotificationTabPane.scss';

import React, { useMemo, useState } from 'react';
import ActionMenu, { ActionMenuItemProps } from '../../gfcomponents/GfActionMenu/GfActionMenu';
import { NotificationTabMenu } from './NotificationTabMenu';
import { Tab } from 'semantic-ui-react';
import NotificationList from './NotificationList';
import { markAsRead, markAsUnread } from '../actions';
import { ActionMenuType, NotificationData, NotificationMenuItem, NotificationsDispatch } from '../notifications-types';
import { MENU_ITEM } from '../data';
import NotificationsEmptyState from './NotificationEmptyState';
import Loader from '../../components/loader';
import { useAppSelector } from '../../hooks/ReduxHooks';

export type NotificationTabPaneProps = {
  notificationDataList: NotificationData[];
  hasUnread: boolean;
  loadingText: string;
  handleNotificationCenterOpen: (isOpen: boolean) => VoidFunc;
  handleActionMenuOpen: (isOpen: boolean) => VoidFunc;
  handleNotificationDeleteModal: (isOpen: boolean, notifications: NotificationData[], actionMenuType?: ActionMenuType) => VoidFunc;
  dispatch: NotificationsDispatch;
};

export default function NotificationTabPane(props: NotificationTabPaneProps) {
  const [menuItem, setMenuItem] = useState<NotificationMenuItem>(MENU_ITEM[0]);
  const isLoading = useAppSelector((state) => state.notificationCenter.isLoading);

  const { handleNotificationCenterOpen, notificationDataList, hasUnread, loadingText, handleNotificationDeleteModal, handleActionMenuOpen, dispatch } = props;

  const filteredNotifications = notificationDataList.filter((o) => {
    if (menuItem.index === 0) {
      // want all of them
      return true;
    }
    // filter unread
    return !o.read;
  });

  const handleDisableActionMenu = (actionMenuItems: ActionMenuItemProps[]) => {
    return actionMenuItems?.length == 0;
  };

  const actionMenuItems: ActionMenuItemProps[] = useMemo(() => {
    const { index } = menuItem;
    let actions: ActionMenuItemProps[] = [];

    // Common actions for all notifications
    if (notificationDataList?.length > 0) {
      actions.push(
        { name: 'Mark all read', icon: 'check', onClick: () => markAsRead(notificationDataList, dispatch) },
        { name: 'Mark all unread', icon: 'bell outline', onClick: () => markAsUnread(notificationDataList, dispatch) }
      );
    }

    // Additional action for read notifications
    if (index === 0 && notificationDataList?.some((n) => n.read)) {
      actions.push({
        name: 'Clear all read notifications',
        icon: 'bell slash outline',
        onClick: () =>
          handleNotificationDeleteModal(
            true,
            notificationDataList.filter((n) => n.read),
            { name: 'Bulk' }
          )()
      });
    }

    // Actions specific to unread notifications
    if (index === 1 && hasUnread) {
      actions = [{ name: 'Mark all read', icon: 'check', onClick: () => markAsRead(notificationDataList, dispatch) }];
    }

    return actions;
  }, [menuItem, notificationDataList, hasUnread, dispatch, handleNotificationDeleteModal]);
  return (
    <div className="notification-tab-pane">
      <div className="tab-action-container">
        <NotificationTabMenu menuItems={MENU_ITEM} activeItem={menuItem} onMenuItemClick={(item) => setMenuItem(item)} />
        <div className="action-menu-container">
          <ActionMenu
            vertical={true}
            className="notification-tab-pane"
            menuClassName="notification-tab-pane-content"
            items={actionMenuItems}
            position="bottom left"
            onActionMenuOpen={handleActionMenuOpen(true)}
            onActionMenuClose={handleActionMenuOpen(false)}
            disabled={handleDisableActionMenu(actionMenuItems)}
          />
        </div>
      </div>
      <Tab.Pane>
        <NotificationsEmptyState isLoading={isLoading} filteredNotifications={filteredNotifications} />
        {filteredNotifications.length > 0 && (
          <div className="tab-container">
            <div className="notifications-container">
              {isLoading && <Loader isLoading={isLoading} />}
              <NotificationList
                notifications={filteredNotifications}
                handleNotificationCenterOpen={handleNotificationCenterOpen}
                loadingText={loadingText}
                handleActionMenuOpen={handleActionMenuOpen}
                handleNotificationDeleteModal={handleNotificationDeleteModal}
              />
            </div>
          </div>
        )}
      </Tab.Pane>
    </div>
  );
}
